import { useMemo, useState } from 'react';
import { Button } from '@/components/atoms/button';
import { Input } from '@/components/atoms/input';
import { Label } from '@/components/atoms/label';
import { useConnectUrlPlatform } from '@/hooks/review-platforms/useConnectPlatform';
import { PLATFORM_CAPABILITIES, type ReviewPlatform } from '@/types/review-platform';

interface UrlPasteFormProps {
  platform: ReviewPlatform; // YELP | BBB | THUMBTACK
  locationId: string | null;
  onSuccess?: () => void;
}

/**
 * Form for the URL-paste connect flow. Performs a live client-side
 * `URL.parse`-style validation and previews the parsed business identifier
 * before submitting. The backend re-validates and persists.
 */
export function UrlPasteForm({ platform, locationId, onSuccess }: UrlPasteFormProps) {
  const cap = PLATFORM_CAPABILITIES[platform];
  const [profileUrl, setProfileUrl] = useState('');
  const mutation = useConnectUrlPlatform(locationId);

  const validation = useMemo(() => validate(platform, profileUrl), [platform, profileUrl]);

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!validation.ok) return;
    mutation.mutate(
      {
        platform,
        profileUrl: profileUrl.trim(),
        ...(locationId ? { locationId } : {}),
      },
      { onSuccess: () => onSuccess?.() },
    );
  }

  return (
    <form className="space-y-4" onSubmit={handleSubmit}>
      <div className="space-y-1.5">
        <Label htmlFor={`${platform}-url`}>{cap.label} profile URL</Label>
        <Input
          id={`${platform}-url`}
          type="url"
          inputMode="url"
          autoFocus
          placeholder={PLACEHOLDERS[platform]}
          value={profileUrl}
          onChange={(e) => setProfileUrl(e.target.value)}
          disabled={mutation.isPending}
        />
        <p className="text-xs text-muted-foreground">
          Paste the public {cap.label} listing URL. We&apos;ll extract the business ID and verify it
          resolves before saving.
        </p>
      </div>

      {profileUrl.trim().length > 0 && (
        <div
          className={`rounded-md border p-3 text-xs ${
            validation.ok
              ? 'border-green-200 bg-green-50 text-green-900'
              : 'border-amber-200 bg-amber-50 text-amber-900'
          }`}
        >
          {validation.ok ? (
            <>
              Looks like a {cap.label} listing. Detected ID:{' '}
              <code className="font-mono">{validation.businessId}</code>
            </>
          ) : (
            validation.message
          )}
        </div>
      )}

      <div className="flex justify-end gap-2">
        <Button type="submit" disabled={!validation.ok || mutation.isPending}>
          {mutation.isPending ? 'Connecting…' : 'Connect'}
        </Button>
      </div>
    </form>
  );
}

const PLACEHOLDERS: Record<'YELP' | 'BBB' | 'THUMBTACK' | string, string> = {
  YELP: 'https://www.yelp.com/biz/your-business-name',
  BBB: 'https://www.bbb.org/us/state/city/business/category/your-business',
  THUMBTACK: 'https://www.thumbtack.com/your-pro-profile-url',
};

interface ValidationOk {
  ok: true;
  businessId: string;
}

interface ValidationFail {
  ok: false;
  message: string;
}

type ValidationResult = ValidationOk | ValidationFail;

function validate(platform: ReviewPlatform, raw: string): ValidationResult {
  const trimmed = raw.trim();
  if (trimmed.length === 0) {
    return { ok: false, message: 'Please paste a URL.' };
  }
  let url: URL;
  try {
    url = new URL(trimmed);
  } catch {
    return { ok: false, message: 'That doesn’t look like a valid URL.' };
  }
  if (url.protocol !== 'https:' && url.protocol !== 'http:') {
    return { ok: false, message: 'URL must start with https://.' };
  }
  const host = url.hostname.toLowerCase();

  switch (platform) {
    case 'YELP': {
      if (!host.includes('yelp.com')) {
        return { ok: false, message: 'Expected a yelp.com URL.' };
      }
      const match = url.pathname.match(/\/biz\/([^/?#]+)/);
      if (!match) {
        return {
          ok: false,
          message: 'Yelp URLs should look like https://www.yelp.com/biz/<your-business>.',
        };
      }
      return { ok: true, businessId: match[1] };
    }
    case 'BBB': {
      if (!host.includes('bbb.org')) {
        return { ok: false, message: 'Expected a bbb.org URL.' };
      }
      const match = url.pathname.match(/\/business\/([^/?#]+)/);
      if (!match) {
        return {
          ok: false,
          message: 'BBB URLs should include /business/<id>.',
        };
      }
      return { ok: true, businessId: match[1] };
    }
    case 'THUMBTACK': {
      if (!host.includes('thumbtack.com')) {
        return { ok: false, message: 'Expected a thumbtack.com URL.' };
      }
      const seg = url.pathname.split('/').filter(Boolean).pop();
      if (!seg) {
        return { ok: false, message: 'Could not extract a profile slug.' };
      }
      return { ok: true, businessId: seg };
    }
    default:
      return { ok: false, message: 'This platform is not URL-paste.' };
  }
}
